import { Link } from "@tanstack/react-router";
import { MessageSquare, Plug, Rss, Zap } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { ClientAppIcon } from "@/components/agents/app-icons";
import { LobuMark } from "@/components/brand/lobu-mark";
import {
	ConnectorMark,
	resolveConnectorDisplay,
} from "@/components/connectors/connector-display";
import { PlatformIcon } from "@/components/devices/device-display";
import type { ConnectorDefinitionItem } from "@/lib/api/connections";
import type { PartialEventFilters } from "@/lib/event-filters";
import type { SourceAttribution } from "@/lib/source-attribution";
import { cn } from "@/lib/utils";

/** Recorded provenance: Activity supplies its existing filter updater; other hosts link to the source. */
export function SourceAttributionRow({
	owner,
	source,
	connectorDefinitions = [],
	className,
	onFiltersChange,
	variant = "inline",
	hideOriginator = false,
	runId,
	onRunSelect,
}: {
	owner: string;
	source: SourceAttribution;
	connectorDefinitions?: ConnectorDefinitionItem[] | null;
	className?: string;
	variant?: "inline" | "chips";
	onFiltersChange?: (updates: PartialEventFilters) => void;
	/** The host already names the originator (e.g. one app's page). */
	hideOriginator?: boolean;
	/** Run this event belongs to. Rendered as a chip opening its conversation
	 *  when onRunSelect is given (the card builds it from its peek context);
	 *  otherwise no chip. The row itself stays hook-free so entity panels and
	 *  notifications keep their no-hook render contracts. */
	runId?: number | null;
	/** Open this run's conversation. */
	onRunSelect?: () => void;
}) {
	const workspaceOwner = owner === "me" ? undefined : owner;
	const connectorKey = source.platform?.trim() || null;
	const display = connectorKey
		? resolveConnectorDisplay(connectorKey, connectorDefinitions ?? [])
		: null;
	const connectionLabel =
		source.connection_name || display?.name || connectorKey;
	const feedLabel = source.feed_name || source.feed_key || null;
	const connectionHref =
		workspaceOwner && connectorKey && source.connection_id != null
			? `/${workspaceOwner}/connectors/${encodeURIComponent(connectorKey)}/${source.connection_id}`
			: null;
	const chips: Array<{ key: string; node: ReactNode }> = [];
	const addChip = ({
		key,
		label,
		content,
		href,
		filters,
		detail = label,
		search,
		onSelect,
	}: {
		key: string;
		label: string;
		content: ReactNode;
		href: string | null;
		filters?: PartialEventFilters;
		detail?: string;
		search?: Record<string, string | number | undefined>;
		/** Host-owned action (e.g. open a run conversation). Wins over href. */
		onSelect?: () => void;
	}) => {
		const chipClass = cn(
			"inline-flex min-w-0 items-center gap-1",
			variant === "chips" && "rounded-md bg-muted px-1.5 py-1",
		);
		chips.push({
			key,
			node:
				onSelect != null ? (
					<button
						type="button"
						className={`${chipClass} hover:underline`}
						title={detail}
						aria-label={detail}
						onClick={onSelect}
					>
						{content}
					</button>
				) : onFiltersChange && filters ? (
					<button
						type="button"
						className={`${chipClass} text-left hover:underline`}
						title={`Filter activity by ${label}`}
						aria-label={`Filter activity by ${label}`}
						onClick={() => onFiltersChange(filters)}
					>
						{content}
					</button>
				) : href ? (
					<Link
						to={href as "/"}
						search={search}
						className={`${chipClass} hover:underline`}
						title={detail}
					>
						{content}
					</Link>
				) : (
					<span className={chipClass} title={detail}>
						{content}
					</span>
				),
		});
	};
	if (connectorKey && connectionLabel) {
		addChip({
			key: "connection",
			label: connectionLabel,
			content: (
				<>
					<ConnectorMark
						plain
						name={display?.name ?? connectorKey}
						icon={display?.icon}
						faviconDomain={display?.faviconDomain}
						fallback={<Plug className="h-3.5 w-3.5" />}
						className="h-3.5 w-3.5 rounded-sm text-[9px]"
					/>
					<span className="truncate">{connectionLabel}</span>
				</>
			),
			href: connectionHref,
			// The existing connector filter resolves connection IDs. A label on
			// an unconnected event is provenance, not a filterable connection.
			filters:
				source.connection_id != null
					? {
							platforms: [connectorKey],
							connectionIds: [source.connection_id],
							feedIds: undefined,
						}
					: undefined,
		});
	}
	if (feedLabel) {
		addChip({
			key: "feed",
			label: feedLabel,
			content: (
				<>
					<Rss className="h-3.5 w-3.5 shrink-0" />
					<span className="truncate">{feedLabel}</span>
				</>
			),
			href: connectionHref && source.feed_id != null ? connectionHref : null,
			filters:
				source.feed_id != null
					? {
							platforms: connectorKey ? [connectorKey] : undefined,
							connectionIds:
								source.connection_id != null
									? [source.connection_id]
									: undefined,
							feedIds: [source.feed_id],
						}
					: undefined,
			search: { feedId: source.feed_id ?? undefined },
		});
	}
	if (source.automation_name) {
		addChip({
			key: "automation",
			label: source.automation_name,
			content: (
				<>
					<Zap className="h-3.5 w-3.5 shrink-0" />
					<span className="truncate">{source.automation_name}</span>
				</>
			),
			href:
				workspaceOwner && source.automation_id != null
					? `/${workspaceOwner}/automations/${source.automation_id}`
					: null,
			filters:
				source.automation_id != null
					? { producedByAutomationId: source.automation_id }
					: undefined,
			search: { view: "definition", run_id: undefined },
		});
	}
	if (runId != null && onRunSelect) {
		const automationLabel =
			source.automation_name ?? `Automation ${source.automation_id}`;
		addChip({
			key: "run",
			label: `Run #${runId}`,
			content: (
				<>
					<MessageSquare className="h-3.5 w-3.5 shrink-0" />
					<span className="truncate">Run #{runId}</span>
				</>
			),
			href: null,
			detail: `${automationLabel} · Run #${runId} conversation`,
			onSelect: onRunSelect,
		});
	}
	const agentLabel = source.agent_name || source.agent_id || null;
	const clientLabel = source.client_name || source.client_id || null;
	const deviceLabel = source.device_label || source.device_worker_id || null;
	const primary = agentLabel
		? { kind: "agent", label: agentLabel }
		: clientLabel
			? { kind: "client", label: clientLabel }
			: deviceLabel
				? { kind: "device", label: deviceLabel }
				: null;
	if (primary && !hideOriginator) {
		const detail = [
			primary.kind === "agent" && clientLabel
				? `${primary.label} via ${clientLabel}`
				: primary.label,
			primary.kind !== "device" && deviceLabel
				? `executed on ${deviceLabel}`
				: null,
		]
			.filter(Boolean)
			.join(" · ");
		const href = !workspaceOwner
			? null
			: primary.kind === "agent" && source.agent_id
				? `/${workspaceOwner}/agents/${encodeURIComponent(source.agent_id)}`
				: primary.kind === "client" && source.client_id
					? `/${workspaceOwner}/agents/connected/${encodeURIComponent(source.client_id)}`
					: primary.kind === "device" && source.device_worker_id
						? `/${workspaceOwner}/connectors/device/${encodeURIComponent(source.device_worker_id)}`
						: null;
		addChip({
			key: "originator",
			label: primary.label,
			content: (
				<>
					{primary.kind === "agent" ? (
						<LobuMark className="h-3.5 w-3.5 shrink-0" />
					) : primary.kind === "client" ? (
						<ClientAppIcon name={primary.label} />
					) : (
						<PlatformIcon
							platform={source.device_platform ?? null}
							className="h-3.5 w-3.5"
						/>
					)}
					<span className="truncate">{primary.label}</span>
				</>
			),
			href,
			filters:
				primary.kind === "agent" && source.agent_id
					? { agentId: source.agent_id }
					: primary.kind === "client" && source.client_id
						? { clientIds: [source.client_id] }
						: undefined,
			detail,
		});
	}
	if (chips.length === 0) return null;
	return (
		<div
			className={cn(
				"flex min-w-0 flex-wrap items-center gap-1 text-muted-foreground",
				className,
			)}
		>
			{chips.map(({ key, node }, index) => (
				<Fragment key={key}>
					{variant === "inline" && index > 0 ? (
						<span className="shrink-0 text-muted-foreground/50">·</span>
					) : null}
					{node}
				</Fragment>
			))}
		</div>
	);
}
